// AccountLayout: Dedicated layout for account management pages
// Provides consistent navigation and layout for all account sections

import React, { useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useSubscription } from '@/hooks/useSubscription'
import { useCredits } from '@/hooks/useCredits'
import {
  ArrowLeft,
  CreditCard,
  Settings,
  Shield,
  HelpCircle,
  Menu,
  X,
  Crown,
  Activity,
  User
} from 'lucide-react'

export type AccountPage = 'subscription' | 'settings' | 'security' | 'help' | 'pricing'

interface AccountLayoutProps {
  activePage: AccountPage
  onPageChange: (page: AccountPage) => void
  onBackToApp: () => void
  children: React.ReactNode
}

interface NavigationItem {
  id: AccountPage
  name: string
  icon: React.ComponentType<any>
  description: string
}

const navigationItems: NavigationItem[] = [
  {
    id: 'subscription',
    name: 'Subscription',
    icon: CreditCard,
    description: 'Billing, plans, and payment methods'
  },
  {
    id: 'settings',
    name: 'Account Settings',
    icon: Settings,
    description: 'Profile, preferences, and notifications'
  },
  {
    id: 'security',
    name: 'Security',
    icon: Shield,
    description: 'Password and account security'
  },
  {
    id: 'help',
    name: 'Help & Support',
    icon: HelpCircle,
    description: 'FAQ, contact, and resources'
  },
  {
    id: 'pricing',
    name: 'Plans & Pricing',
    icon: Crown,
    description: 'Compare plans and upgrade subscription'
  }
]

export function AccountLayout({
  activePage,
  onPageChange,
  onBackToApp,
  children
}: AccountLayoutProps) {
  const { user } = useAuth()
  const { subscription } = useSubscription()
  const { balance } = useCredits()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const getUserInitials = () => {
    if (!user?.user_metadata?.full_name && !user?.email) return 'U'
    const name = user.user_metadata?.full_name || user.email
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  const getUserDisplayName = () => {
    return user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User'
  }

  const getCurrentPageTitle = () => {
    return navigationItems.find(item => item.id === activePage)?.name || 'Account'
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col lg:flex-row">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b px-4 py-3 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center space-x-3">
          <button
            onClick={onBackToApp}
            className="p-2 -ml-2 text-gray-600 hover:text-gray-900 transition-colors"
            aria-label="Back to app"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">{getCurrentPageTitle()}</h1>
        </div>
        <button
          onClick={toggleMobileMenu}
          className="p-2 text-gray-600 hover:text-gray-900 transition-colors"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? (
            <X className="w-5 h-5" />
          ) : (
            <Menu className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Mobile Navigation Overlay */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black bg-opacity-50" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="bg-white w-80 h-full shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-4">
              {/* Mobile User Info */}
              <div className="flex items-center space-x-3 mb-6">
                {user?.user_metadata?.avatar_url ? (
                  <img
                    src={user.user_metadata.avatar_url}
                    alt="Profile"
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium">
                    {getUserInitials()}
                  </div>
                )}
                <div>
                  <p className="font-medium text-gray-900">{getUserDisplayName()}</p>
                  <p className="text-sm text-gray-600">{user?.email}</p>
                </div>
              </div>

              {/* Mobile Navigation Items */}
              <nav className="space-y-1">
                {navigationItems.map((item) => {
                  const Icon = item.icon
                  const isActive = activePage === item.id
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        onPageChange(item.id)
                        setIsMobileMenuOpen(false)
                      }}
                      className={`w-full flex items-center space-x-3 px-3 py-3 rounded-lg text-left transition-colors ${
                        isActive
                          ? 'bg-blue-50 text-blue-700 border border-blue-200'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <Icon className={`w-5 h-5 ${isActive ? 'text-blue-600' : 'text-gray-500'}`} />
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-xs opacity-75">{item.description}</p>
                      </div>
                    </button>
                  )
                })}
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-80 bg-white border-r border-gray-200">
        {/* Desktop Header */}
        <div className="p-6 border-b border-gray-200">
          <button
            onClick={onBackToApp}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors mb-6 group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm font-medium">Back to StockTagger.ai</span>
          </button>

          {/* User Account Overview */}
          <div className="flex items-center space-x-3 mb-4">
            {user?.user_metadata?.avatar_url ? (
              <img
                src={user.user_metadata.avatar_url}
                alt="Profile"
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium">
                {getUserInitials()}
              </div>
            )}
            <div>
              <p className="font-medium text-gray-900">{getUserDisplayName()}</p>
              <p className="text-sm text-gray-600">{user?.email}</p>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="flex items-center space-x-2 bg-gray-50 rounded-lg p-2">
              <Crown className="w-4 h-4 text-blue-600" />
              <span className="text-gray-700 font-medium">
                {subscription?.plan_name || 'Free Trial'}
              </span>
            </div>
            <div className="flex items-center space-x-2 bg-gray-50 rounded-lg p-2">
              <Activity className="w-4 h-4 text-green-600" />
              <span className="text-gray-700 font-medium">
                {balance?.balance?.toLocaleString() || '0'} credits
              </span>
            </div>
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {navigationItems.map((item) => {
            const Icon = item.icon
            const isActive = activePage === item.id
            return (
              <button
                key={item.id}
                onClick={() => onPageChange(item.id)}
                className={`w-full flex items-start space-x-3 px-3 py-3 rounded-lg text-left transition-colors ${
                  isActive
                    ? 'bg-blue-50 text-blue-700 border border-blue-200'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <Icon className={`w-5 h-5 mt-0.5 ${isActive ? 'text-blue-600' : 'text-gray-500'}`} />
                <div>
                  <p className="font-medium mb-1">{item.name}</p>
                  <p className="text-sm opacity-75 leading-relaxed">{item.description}</p>
                </div>
              </button>
            )
          })}
        </nav>

        {/* Desktop Footer */}
        <div className="p-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            StockTagger.ai Account Settings
          </p>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 lg:overflow-hidden">
        <div className="h-full lg:overflow-y-auto">
          {/* Desktop Page Header - Hidden on mobile */}
          <div className="hidden lg:block bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">{getCurrentPageTitle()}</h1>
                <p className="text-gray-600 text-sm mt-1">
                  {navigationItems.find(item => item.id === activePage)?.description}
                </p>
              </div>
            </div>
          </div>

          {/* Page Content */}
          <div className="p-4 lg:p-6">
            <div className="max-w-4xl mx-auto">
              {children}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default AccountLayout